import { FunctionComponent, useCallback } from "react";
import { Text } from "@ui-kit/Text/Text";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { useTranslation } from "react-i18next";
import { formatDistance } from "@lib/calculateDistance";
import { IconButton } from "@ui-kit/IconButton/IconButton";
import { ChevronLeftIcon } from "@icons/ChevronLeftIcon";
import { ChevronRightIcon } from "@icons/ChevronRightIcon";
import { TravelModeToggle } from "./TravelModeToggle";

import "./DirectionListHeader.css";
import { TabButton } from "@ui-kit/TabButton/TabButton";

const styles = {} as any;

interface DirectionListHeaderProps {
  /**
   * Change current stop. Either `-1` or `1` to go back or forward respectively.
   */
  onSetStopDirections: (direction: number) => void;
  /**
   * Current directions list/stop index.
   */
  currentStopIndex: number;
  /**
   * Total number of directions lists/stops.
   */
  stopsCount: number;
  /**
   * Name of the current directions list stop.
   */
  currentStopName: string;
  /**
   * Total time in seconds to reach stop.
   */
  timeInSeconds: number;
  /**
   * Total distance in meters for the steps to reach stop.
   */
  distanceInMeters: number;
  /**
   * Callback when End Route button is pressed.
   */
  onEndRoutePress: () => void;
}

const DirectionsTime: FunctionComponent<{ timeInSeconds: number }> = ({
  timeInSeconds,
}) => {
  const timespanMinutesRounded = Math.ceil(timeInSeconds / 60);
  const { t } = useTranslation();

  if (timespanMinutesRounded < 60) {
    return (
      <>
        <div style={styles.minorContainer}>
          <Text style={styles.minorTitle} variant="fontHeadingSmall">
            {timespanMinutesRounded}
          </Text>
        </div>
        <div style={styles.minorContainer}>
          <Text style={styles.minorSubtitle} variant="fontBodySmall">
            {t("units.short.minutes")}
          </Text>
        </div>
      </>
    );
  }

  const hours = Math.floor(timespanMinutesRounded / 60);
  const minutes = timespanMinutesRounded - hours * 60;

  return (
    <>
      <div style={styles.minorContainer}>
        <Text style={styles.minorTitle} variant="fontHeadingSmall">
          {hours}:{minutes}
        </Text>
      </div>
      <div style={styles.minorContainer}>
        <Text style={styles.minorSubtitle} variant="fontBodySmall">
          {t("units.short.hours")}
        </Text>
      </div>
    </>
  );
};

const DirectionsDistance: FunctionComponent<{ distanceInMeters: number }> = ({
  distanceInMeters,
}) => {
  const distance = formatDistance(distanceInMeters);
  return (
    <>
      <div style={styles.minorContainer}>
        <Text style={styles.minorTitle} variant="fontHeadingSmall">
          {distance.distanceFormatted}
        </Text>
      </div>
      <div style={styles.minorContainer}>
        <Text style={styles.minorSubtitle} variant="fontBodySmall">
          {distance.unit}
        </Text>
      </div>
    </>
  );
};

const DirectionListHeader: FunctionComponent<DirectionListHeaderProps> = ({
  onSetStopDirections,
  currentStopIndex,
  currentStopName,
  stopsCount,
  distanceInMeters,
  timeInSeconds,
  onEndRoutePress,
}) => {
  const { t } = useTranslation();
  const handleSetStopPress = useCallback(
    (direction: number) => () => {
      onSetStopDirections(direction);
    },
    [onSetStopDirections],
  );

  const handleEndRoutePress = useCallback(() => {
    onEndRoutePress();
  }, [onEndRoutePress]);

  return (
    <div className="direction-list-header-component">
      <div className="title-header-container">
        <IconButton
          icon={
            <ChevronLeftIcon
              color={currentStopIndex === 0 ? colors.neutral600 : colors.black}
              size={18}
            />
          }
          type="flat"
          disabled={currentStopIndex === 0}
          onPress={handleSetStopPress(-1)}
        />
        <div className="title-container">
          <Text style={styles.titleText} variant="fontHeadingSmall">
            {t("screens.planNavigate.nextStop")}: {currentStopName}
          </Text>
        </div>
        <IconButton
          icon={
            <ChevronRightIcon
              size={18}
              color={
                currentStopIndex === stopsCount - 1
                  ? colors.neutral600
                  : colors.black
              }
            />
          }
          type="flat"
          disabled={currentStopIndex === stopsCount - 1}
          onPress={handleSetStopPress(1)}
        />
      </div>
      <div className="travel-mode-toggle-container">
        <TravelModeToggle />
      </div>
      <div className="summary-container">
        <div>
          <DirectionsTime timeInSeconds={timeInSeconds} />
        </div>
        <div>
          <DirectionsDistance distanceInMeters={distanceInMeters} />
        </div>
        <div className="end-route-container">
          <TabButton
            variant="destructive"
            onPress={handleEndRoutePress}
            label={t("screens.planNavigate.endRoute")}
          />
        </div>
      </div>
    </div>
  );
};

export { DirectionListHeader };
